import { FC, useEffect } from 'react'
import {
  MeetingType,
  MeetingStepStatus,
  MeetingStepViewId,
  GetManagerMeetingStepTargetsQuery,
  ObjectiveStatus,
  Currency,
  BonusBetweenAnnualBonusAndProrataDto,
} from '../../../../types'
import { useAuth } from '../../../../context/auth-context'
import { useAmountAndCurrency } from '../../../../shared/useAmountAndCurrency'
import { calculateNewTargetsData } from '../shared/util'
import { ManagerNewObjectives } from '../../okr/ManagerNewObjectives'
import { ClosedTargetsComponent } from '../../okr/closedTargets/ClosedTargetsComponent'
import { LoadingAndErrors } from '../../../../components/custom_components/LoadingAndErrors'
import shallow from 'zustand/shallow'
import AppraisalFooter from '../../../../components/custom_components/Appraisal/AppraisalFooter'
import PerFlexRow from '../../../../components/custom_components/FlexRow'
import MeetingBackground from '../../../../components/custom_components/Meeting/MeetingBackground'
import { useTargetsModalStore } from '../../../../components/custom_components/TargetsModal/TargetsModalStore'
import { useCurrency } from '../../../../context/currency-context'
import useManagerMeetingStepTargets from '../../../../graphql/appraisal-flow/hooks/useManagerMeetingStepTargets'
import { retrieveContractStartAndEndDateFromContract } from '../../../profile/contract/utils/contractUtil'
import { TargetBonusOverview } from '../shared/appraisal-components/TargetBonusOverview'
import { filterObjectivesByTypeStatusAndRefYear } from '../../okr/util'
import { useBonusDistributionModalStore } from '../../../../components/custom_components/BonusDistributionModal/BonusDistributionModalStore'
import {
  useFindBonusByEmployeeIdAndMeetingId,
  useFlexibleProrataManagementEnabled,
} from '../shared/appraisal-components/useBonusAmountEditableIsEnabled'
import { MeetingStepDefaultProps } from '../shared/appraisal-types'
import { AppraisalFlowHelper } from '../shared/AppraisalFlowHelper'
import { NewTargetsFooter } from '../../../../components/custom_components/NewTargetsFooter'
import { useTranslation } from 'react-i18next'
import { useCollapse } from '../../../../routes/collapse-context'
import { ContentContainer } from '../../../../components/custom_components/ContentContainer'
import { isDefined } from '../../../../types.helper'
import { getFlexibleBonusUnknownCurrencyForConversion, toEligibleFlexibleBonusOverride } from '../shared/flexibleBonusUtil'

interface ManagerMeetingStepNewGoalsContentProps {
  data: GetManagerMeetingStepTargetsQuery
}

const ManagerMeetingStepNewGoals: FC<MeetingStepDefaultProps> = ({ meeting, backBtn, nextBtn }) => {
  const collapsed = useCollapse()

  const { data, loading, error } = useManagerMeetingStepTargets(
    meeting.employee.id,
    meeting.id,
    meeting.targetsCalculationHelper.newTargetsReferenceYear,
    meeting.employee.currentContract!.legalEntity.country,
    true,
  )

  if (loading || error || !data) {
    return (
      <ContentContainer collapsed={collapsed}>
        <LoadingAndErrors isLoading={loading} hasNoData={!data} error={error} />
      </ContentContainer>
    )
  } else {
    return <ManagerMeetingStepNewGoalsContent data={data} meeting={meeting} backBtn={backBtn} nextBtn={nextBtn} />
  }
}

const ManagerMeetingStepNewGoalsContent: FC<MeetingStepDefaultProps & ManagerMeetingStepNewGoalsContentProps> = ({
  data,
  meeting,
  backBtn,
  nextBtn,
}) => {
  const {
    data: bonusAmountEditableData,
    error: bonusAmountEditableError,
    loading: bonusAmountEditableLoading,
  } = useFlexibleProrataManagementEnabled(data.currentContract!.legalEntity.country)

  const {
    data: findBonusAmountData,
    error: findBonusAmountDataError,
    loading: findBonusAmountLoading,
  } = useFindBonusByEmployeeIdAndMeetingId(meeting.employee.id, meeting.id)

  const { currency, currencyConversionData } = useCurrency()

  if (
    bonusAmountEditableError ||
    findBonusAmountDataError ||
    bonusAmountEditableLoading ||
    findBonusAmountLoading ||
    !bonusAmountEditableData ||
    !findBonusAmountData
  ) {
    return (
      <LoadingAndErrors
        isLoading={bonusAmountEditableLoading || findBonusAmountLoading}
        hasNoData={!bonusAmountEditableData || !findBonusAmountData}
        error={bonusAmountEditableError || findBonusAmountDataError}
        showSpinner
      />
    )
  }

  const currentYearFlexibleBonus = findBonusAmountData.findBonusByEmployeeIdInAppraisalSeasonOfMeeting
  const flexibleBonusUnknownCurrency = getFlexibleBonusUnknownCurrencyForConversion(
    currentYearFlexibleBonus,
    currency,
    currencyConversionData,
  )

  return (
    <ManagerMeetingStepNewGoalsContentPage
      data={data}
      meeting={meeting}
      backBtn={backBtn}
      nextBtn={nextBtn}
      currentYearFlexibleBonus={currentYearFlexibleBonus}
      flexibleBonusUnknownCurrency={flexibleBonusUnknownCurrency}
      bonusEditable={bonusAmountEditableData.isFlexibleProrataManagementEnabled}
    />
  )
}

interface ManagerMeetingStepNewGoalsContentPageProps {
  currentYearFlexibleBonus?: BonusBetweenAnnualBonusAndProrataDto | null
  flexibleBonusUnknownCurrency: Currency | undefined
  bonusEditable: boolean
}

const ManagerMeetingStepNewGoalsContentPage: FC<
  MeetingStepDefaultProps & ManagerMeetingStepNewGoalsContentProps & ManagerMeetingStepNewGoalsContentPageProps
> = ({ data, meeting, nextBtn, backBtn, currentYearFlexibleBonus, flexibleBonusUnknownCurrency, bonusEditable }) => {
  const { t } = useTranslation()
  const { user } = useAuth()
  const { currencyConversionData } = useCurrency()
  const { formatCurrency } = useAmountAndCurrency(user.locale)

  const [setModalProps, cleanModalProps] = useTargetsModalStore((state) => [state.setModalProps, state.cleanModalProps], shallow)

  const isAppraisalUpdate = meeting.type === MeetingType.AppraisalUpdate

  const referenceYear = meeting.targetsCalculationHelper.newTargetsReferenceYear
  const {
    objectivesInMeeting,
    contractChangesByEmployee,
    targetBonusOfYear,
    currentContract,
    salaryOfYear,
    branchOfficeByEmployee,
    isBonusValidationEnabled,
    contractsInYear,
  } = data

  const currencyForFooter = currentContract!.currencyForPayments

  const [contractStartDate, contractEndDate] = retrieveContractStartAndEndDateFromContract(currentContract)

  const {
    id: meetingId,
    targetsCalculationHelper: { newTargetsReferenceYear },
    employeePrepared,
  } = meeting

  let status = isAppraisalUpdate
    ? [
        ObjectiveStatus.Approved,
        ObjectiveStatus.Proposal,
        ObjectiveStatus.ProposalFromManager,
        ObjectiveStatus.MarkedAsDone,
        ObjectiveStatus.DismissedPending,
      ]
    : [ObjectiveStatus.Proposal, ObjectiveStatus.ProposalFromManager, ObjectiveStatus.Approved, ObjectiveStatus.MarkedAsDone]

  if (!employeePrepared) {
    // we filter proposal targets if employee didn't confirm preparation (reviewer can't see these targets so they aren't valid)
    status = status.filter((status) => status !== ObjectiveStatus.Proposal)
  }

  const monetaryObjectives = filterObjectivesByTypeStatusAndRefYear(objectivesInMeeting, true, status, newTargetsReferenceYear)

  const distributionButtonDisabled =
    monetaryObjectives.filter(
      ({ status, targetAmount }) =>
        [ObjectiveStatus.ProposalFromManager, ObjectiveStatus.Approved].includes(status) ||
        (status === ObjectiveStatus.Proposal && isDefined(targetAmount)),
    ).length === 0 ||
    monetaryObjectives.some(
      (obj) =>
        ![ObjectiveStatus.DismissedPending, ObjectiveStatus.Dismissed].includes(obj.status) &&
        obj.currency !== currentContract!.currencyForPayments,
    )

  const openDistributionModal = useBonusDistributionModalStore((state) => state.openDistributionModal)

  const handleDistributionClick = () => {
    const objectives = monetaryObjectives.filter(
      ({ status, targetAmount }) =>
        [ObjectiveStatus.ProposalFromManager, ObjectiveStatus.Approved].includes(status) ||
        (status === ObjectiveStatus.Proposal && isDefined(targetAmount)),
    )
    openDistributionModal(meetingId, objectives, targetBonusOfTheYear, alreadyPaidObjectivesAmount, alreadyPlanned)
  }

  const allowedNewTargets = meeting.allowedMonetaryTargets.newTargetsAllowed

  const flexibleBonusOverride = toEligibleFlexibleBonusOverride(
    currentYearFlexibleBonus,
    currentContract!.currencyForPayments,
    currencyConversionData,
    branchOfficeByEmployee.timezone,
    referenceYear,
    contractsInYear,
    targetBonusOfYear,
    contractChangesByEmployee,
  )

  const {
    annualBonus,
    prorataAmount,
    prorataPartials,
    flexibleBonusAmount,
    targetBonusOfTheYear,
    targetBonusRemaining,
    alreadyPlanned,
    hasBonusOrWillHaveBonus,
    alreadyPaidObjectivesAmount,
    alreadyPlannedAndPaid,
  } = calculateNewTargetsData(
    allowedNewTargets,
    branchOfficeByEmployee.timezone,
    isBonusValidationEnabled,
    flexibleBonusOverride,
    objectivesInMeeting,
    targetBonusOfYear,
    salaryOfYear,
    currentContract!.currencyForPayments,
    currentContract && currentContract.annualSalary ? currentContract.annualSalary.amount : 0,
    currentContract && currentContract.targetBonus ? currentContract.targetBonus.amount : 0,
    contractChangesByEmployee,
    isAppraisalUpdate,
    referenceYear,
    contractStartDate,
    currencyConversionData,
    contractEndDate,
    contractsInYear,
    flexibleBonusUnknownCurrency,
  )

  useEffect(() => {
    setModalProps({
      employeeId: meeting.employee.id,
      meetingId: meeting.id,
      isAppraisalUpdate,
      hasBonusOrWillHaveBonus,
      targetBonus: targetBonusOfTheYear,
      alreadyPlanned: alreadyPlannedAndPaid,
      employeeCurrency: currentContract ? currentContract.currencyForPayments : null,
      employeeCountry: meeting.employee.currentContract ? meeting.employee.currentContract.legalEntity.country : null,
      allowedNewTargets,
      disabledMonetaryButtonTooltip: !allowedNewTargets
        ? t('appraisalMeeting.stepTargetAndBonus.disabledMonetaryButton.targetsNotAllowedForLowEmployee')
        : '',
      meeting,
    })
    return () => {
      cleanModalProps()
    }
  }, [
    hasBonusOrWillHaveBonus,
    isAppraisalUpdate,
    meeting.employee.id,
    meeting.id,
    meeting.requiresSpecialHandlingOfLowTag,
    setModalProps,
    targetBonusOfTheYear,
    alreadyPlanned,
    cleanModalProps,
    currentContract,
    meeting,
    objectivesInMeeting,
    referenceYear,
    alreadyPlannedAndPaid,
    allowedNewTargets,
    t,
  ])

  const existingWrongNewTargets =
    !allowedNewTargets &&
    monetaryObjectives.filter(({ status }) => [ObjectiveStatus.ProposalFromManager, ObjectiveStatus.Approved].includes(status)).length > 0

  return (
    <div data-e2e-manager-meeting-step-new-goals>
      <MeetingBackground withFooter={hasBonusOrWillHaveBonus}>
        <TargetBonusOverview
          bonusAmountEditable={bonusEditable}
          flexibleBonusAmount={flexibleBonusAmount}
          prorataPartials={prorataPartials}
          targetBonusList={targetBonusOfYear}
          contractsInYear={contractsInYear}
          openContractChanges={contractChangesByEmployee}
          currentContract={currentContract!}
          meeting={meeting}
          paidOutBonus={alreadyPaidObjectivesAmount}
          plannedBonus={alreadyPlanned}
          remainingBonus={targetBonusRemaining}
          prorataAmount={prorataAmount}
          annualBonus={annualBonus}
          employeeTimezone={branchOfficeByEmployee.timezone}
          distributionAvailable={!!monetaryObjectives.length}
          isAppraisalUpdate={!!isAppraisalUpdate}
          onDistributionClick={handleDistributionClick}
          distributionButtonDisabled={distributionButtonDisabled}
        />

        <ManagerNewObjectives
          meeting={meeting}
          objectives={objectivesInMeeting}
          hasBonusOrWillHaveBonus={hasBonusOrWillHaveBonus}
          isAppraisalUpdate={isAppraisalUpdate}
          existingWrongNewTargets={existingWrongNewTargets}
        />

        {isAppraisalUpdate && <ClosedTargetsComponent objectives={objectivesInMeeting} />}
      </MeetingBackground>

      {hasBonusOrWillHaveBonus && (
        <AppraisalFooter>
          <NewTargetsFooter
            targetBonus={targetBonusOfTheYear}
            plannedBonus={alreadyPlanned}
            remainingBonus={targetBonusRemaining}
            cannotProceed={false}
            formatCurrencyCallback={(amount) => formatCurrency(amount, currencyForFooter)}
          />
        </AppraisalFooter>
      )}

      <PerFlexRow justify="space-between">
        {backBtn && backBtn()}
        {nextBtn && nextBtn(MeetingStepStatus.Done)}
      </PerFlexRow>
      <AppraisalFlowHelper stepViewId={MeetingStepViewId.NewTargets} stepStatus={MeetingStepStatus.Done} />
    </div>
  )
}

export default ManagerMeetingStepNewGoals
